import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Divider, Typography } from "@mui/material";
import type { FunctionComponent } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useSignInMutation } from "Auth/Api/Mutations/useSignInMutation";
import { useEntraIdLogin } from "Auth/Hooks/useEntraIdLogin";
import { BlFormInput } from "Forms/Components/BlFormInput";
import useHandleServerError from "Forms/Hooks/useHandleServerError";
import {
	GlobalResources,
	useResource,
} from "Infrastructure/Translations/Resources";
import { BlButton } from "Shared/Buttons/BlButton";

const SignInSchema = z.object({
	login: z.email(),
	password: z.string().min(3),
});

type SignInFormModel = z.infer<typeof SignInSchema>;

const PageResources = GlobalResources.SignIn;

type Props = {};

export const SignInForm: FunctionComponent<Props> = () => {
	const form = useForm<SignInFormModel>({
		resolver: zodResolver(SignInSchema),
	});
	const {
		control,
		handleSubmit,
		formState: { errors },
	} = form;
	const { t } = useResource();
	const { initiateLogin, isConfigured: isEntraIdConfigured } =
		useEntraIdLogin();

	const { mutate, error } = useSignInMutation();

	const submit = (data: SignInFormModel) => {
		mutate(data);
	};

	useHandleServerError({
		form,
		error,
		formKey: "login",
		resource: PageResources.Error,
	});

	return (
		<Box>
			<Typography variant="h1" mb={4}>
				{t(PageResources.Title)}
			</Typography>

			{isEntraIdConfigured && (
				<>
					<BlButton
						type="button"
						fullWidth
						color="primary"
						variant="outlined"
						onClick={initiateLogin}
						sx={{ mb: 2 }}
					>
						Sign in with Microsoft
					</BlButton>

					<Divider sx={{ my: 3 }}>or</Divider>
				</>
			)}

			<form onSubmit={handleSubmit(submit)}>
				<BlFormInput
					control={control}
					name="login"
					autoComplete="login"
					errors={errors}
					label={t(PageResources.Login.Label)}
				/>

				<BlFormInput
					control={control}
					name="password"
					errors={errors}
					type="password"
					autoComplete="password"
					label={t(PageResources.Password.Label)}
				/>

				<Box m={2} />

				<BlButton type="submit" fullWidth color="primary">
					{t(PageResources.Button)}
				</BlButton>
			</form>
		</Box>
	);
};
